import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import * as clubsApi from '../../api/clubs'
import type { ClubListItem } from '../../api/clubs'

export function ClubDirectory() {
  const [clubs, setClubs] = useState<ClubListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('q') ?? '')
  const [zoneFilter, setZoneFilter] = useState<number | null>(null)
  const [stateFilter, setStateFilter] = useState('')

  useEffect(() => {
    async function load() {
      try {
        const data = await clubsApi.listClubs()
        setClubs(data)
      } catch {
        setError('Failed to load clubs. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  useEffect(() => {
    setSearch(searchParams.get('q') ?? '')
  }, [searchParams])

  const zones = useMemo(() => {
    const set = new Set(clubs.map((c) => c.clubZone))
    return Array.from(set).sort((a, b) => a - b)
  }, [clubs])

  const states = useMemo(() => {
    const set = new Set(clubs.map((c) => c.clubState).filter(Boolean))
    return Array.from(set).sort()
  }, [clubs])

  const filtered = useMemo(() => {
    return clubs.filter((c) => {
      if (search && !c.clubName.toLowerCase().includes(search.toLowerCase())) return false
      if (zoneFilter !== null && c.clubZone !== zoneFilter) return false
      if (stateFilter && c.clubState !== stateFilter) return false
      return true
    })
  }, [clubs, search, zoneFilter, stateFilter])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
        <span className="ml-3 text-text-muted">Loading clubs...</span>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Club Directory - ARPU</title>
        <meta name="description" content="Browse all American Racing Pigeon Union clubs. Find a club by state, zone, or name." />
      </Helmet>

      <div>
        <h1 className="text-2xl font-heading font-bold text-text mb-2">Club Directory</h1>
        <p className="text-text-light mb-6">
          Browse {clubs.length} AU clubs across the country.
        </p>

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-surface border border-border rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by club name..."
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
          <select
            value={zoneFilter ?? ''}
            onChange={(e) => setZoneFilter(e.target.value ? Number(e.target.value) : null)}
            className="w-full md:w-auto px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All Zones</option>
            {zones.map((z) => (
              <option key={z} value={z}>Zone {z}</option>
            ))}
          </select>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="w-full md:w-auto px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All States</option>
            {states.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <p className="text-sm text-text-muted mb-4">
          Showing {filtered.length} of {clubs.length} clubs
        </p>

        {/* Club grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((club) => (
            <Link
              key={club.clubNum}
              to={`/clubs/${club.slug}`}
              className="block p-4 rounded-lg border border-border bg-surface hover:border-primary/30 hover:shadow-sm transition-all"
            >
              <h2 className="font-heading font-semibold text-text mb-1">{club.clubName}</h2>
              <p className="text-sm text-text-light">
                {club.clubState && <span>{club.clubState} &middot; </span>}
                Zone {club.clubZone}
              </p>
              <p className="text-sm text-text-muted mt-1">
                {club.memberCount} member{club.memberCount !== 1 ? 's' : ''}
              </p>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-text-muted">
            No clubs match your filters.
          </div>
        )}
      </div>
    </>
  )
}
